import { useState, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import type { Settings as SettingsType, AIProvider, DemoModeState, Project } from '@shared/types'
import type { LicenseInfo } from '@shared/license-types'
import { useLicense } from '../contexts/LicenseContext'
import { useApi } from '../contexts/ApiContext'

interface LayoutContext {
  refreshProjects: () => Promise<void>
  updateDemoActive: (active: boolean) => void
  projects: Project[]
  isDemoActive: boolean
}

export default function Settings() {
  const [settings, setSettings] = useState<SettingsType>({
    aiProvider: 'azure-openai',
    anthropicApiKey: '',
    openaiApiKey: '',
    azureEndpoint: '',
    azureApiKey: '',
    azureDeployment: '',
    azureApiVersion: '2024-12-01-preview',
  })
  const [saved, setSaved] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null)
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null)
  const [demoState, setDemoState] = useState<DemoModeState | null>(null)
  const [togglingDemo, setTogglingDemo] = useState(false)
  const navigate = useNavigate()
  const { refreshProjects, updateDemoActive } = useOutletContext<LayoutContext>()
  const { refresh: refreshLicense, status: licenseStatus } = useLicense()
  const api = useApi()

  useEffect(() => {
    api.getSettings().then(setSettings)
    api.getLicenseInfo().then(setLicenseInfo)
    api.getDemoState().then(setDemoState)
  }, [api])

  const toggleDemo = async () => {
    if (togglingDemo) return
    setTogglingDemo(true)
    const newActive = !demoState?.active
    setDemoState((prev) => prev ? { ...prev, active: newActive } : null)
    try {
      const result = await api.setDemoActive(newActive)
      if (!result.success) {
        // Revert to authoritative server state on failure
        setDemoState(await api.getDemoState())
        return
      }
      // Trust newActive — re-fetching getDemoState can hit a different replica
      // in multi-instance deployments and return stale state.
      setDemoState((prev) => prev ? { ...prev, active: newActive } : { active: newActive, source: 'settings' })
      if (api.platform === 'web') {
        updateDemoActive(newActive)
      } else {
        if (newActive) await api.listDemoDatasets()
        await refreshProjects()
      }
    } finally {
      setTogglingDemo(false)
    }
  }

  const save = async () => {
    await api.setSettings(settings)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const testConnection = async () => {
    setTesting(true)
    setTestResult(null)
    // Save settings first so Azure config is available to the provider layer
    await api.setSettings(settings)
    const apiKey =
      settings.aiProvider === 'claude' ? settings.anthropicApiKey
      : settings.aiProvider === 'azure-openai' ? settings.azureApiKey
      : settings.openaiApiKey
    const ok = await api.testConnection(settings.aiProvider, apiKey)
    setTestResult({ ok, msg: ok ? 'Connection successful!' : 'Connection failed — check your configuration.' })
    setTesting(false)
  }

  const Field = ({
    label,
    value,
    onChange,
    placeholder,
    hint,
  }: {
    label: string
    value: string
    onChange: (v: string) => void
    placeholder?: string
    hint?: string
  }) => (
    <div className="mb-5">
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white font-mono"
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )

  const isWeb = api.platform === 'web'

  return (
    <div className="p-8 max-w-xl animate-fade-in">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Settings</h1>
      <p className="text-sm text-gray-500 mb-8">
        {isWeb
          ? 'Toggle demo mode for offline presentations and demos.'
          : 'Configure your AI provider and API keys for navigator generation.'}
      </p>

      {/* Demo Mode Toggle */}
      <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Demo Mode</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {demoState?.active
                ? 'Active — talent navigators generated from pre-built sample data. No API key or internet required.'
                : !licenseStatus?.isValid
                  ? 'Requires a valid license.'
                  : 'When enabled, use pre-built talent navigators for offline demos and presentations.'}
            </p>
          </div>
          <button
            role="switch"
            aria-checked={demoState?.active ?? false}
            aria-label="Demo Mode"
            onClick={toggleDemo}
            disabled={togglingDemo || !licenseStatus?.isValid || demoState?.source === 'cli' || demoState?.source === 'env'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              demoState?.active ? 'bg-amber-500' : 'bg-gray-300'
            } ${togglingDemo || !licenseStatus?.isValid || demoState?.source === 'cli' || demoState?.source === 'env' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                demoState?.active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {(demoState?.source === 'cli' || demoState?.source === 'env') && (
          <p className="text-[10px] text-amber-600 mt-1">
            Activated via {demoState.source === 'cli' ? '--demo flag' : 'TALENT_NAV_DEMO environment variable'}
          </p>
        )}
        {!licenseStatus?.isValid && demoState?.source !== 'cli' && demoState?.source !== 'env' && (
          <p className="text-[10px] text-amber-600 mt-1">
            A valid license is required to enable Demo Mode.
          </p>
        )}
      </div>

      {!isWeb && (
        <>
          {/* Provider toggle */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Default AI Provider</label>
            <div className="flex gap-3">
              {(['claude', 'openai', 'azure-openai'] as AIProvider[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setSettings((s) => ({ ...s, aiProvider: p }))}
                  className={`flex-1 py-2.5 px-4 rounded-lg border-2 text-sm font-semibold transition-all ${
                    settings.aiProvider === p
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {p === 'claude' ? '⚡ Claude' : p === 'openai' ? '🧠 GPT-4o' : '☁️ Azure OpenAI'}
                </button>
              ))}
            </div>
          </div>

          {/* API Keys — show fields for selected provider */}
          {settings.aiProvider === 'claude' && (
            <Field
              label="Anthropic API Key"
              value={settings.anthropicApiKey}
              onChange={(v) => setSettings((s) => ({ ...s, anthropicApiKey: v }))}
              placeholder="Enter your Anthropic API key"
              hint="Used when Claude is selected as your AI provider."
            />
          )}
          {settings.aiProvider === 'openai' && (
            <Field
              label="OpenAI API Key"
              value={settings.openaiApiKey}
              onChange={(v) => setSettings((s) => ({ ...s, openaiApiKey: v }))}
              placeholder="sk-..."
              hint="Used when GPT-4o is selected as your AI provider."
            />
          )}
          {settings.aiProvider === 'azure-openai' && (
            <>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Azure Endpoint</label>
                <input
                  type="text"
                  value={settings.azureEndpoint}
                  onChange={(e) => setSettings((s) => ({ ...s, azureEndpoint: e.target.value }))}
                  placeholder="https://your-resource.openai.azure.com/"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white font-mono"
                />
                <p className="text-xs text-gray-400 mt-1">Your Azure OpenAI resource endpoint URL.</p>
              </div>
              <Field
                label="Azure API Key"
                value={settings.azureApiKey}
                onChange={(v) => setSettings((s) => ({ ...s, azureApiKey: v }))}
                placeholder="your-azure-api-key"
                hint="API key from your Azure OpenAI resource."
              />
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Deployment Name</label>
                <input
                  type="text"
                  value={settings.azureDeployment}
                  onChange={(e) => setSettings((s) => ({ ...s, azureDeployment: e.target.value }))}
                  placeholder="csdep-30429803-gpt-4o"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white font-mono"
                />
                <p className="text-xs text-gray-400 mt-1">The deployment name of your Azure OpenAI model.</p>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">API Version</label>
                <input
                  type="text"
                  value={settings.azureApiVersion}
                  onChange={(e) => setSettings((s) => ({ ...s, azureApiVersion: e.target.value }))}
                  placeholder="2024-12-01-preview"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white font-mono"
                />
                <p className="text-xs text-gray-400 mt-1">Azure OpenAI API version.</p>
              </div>
            </>
          )}

          {/* Test + Save */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={testConnection}
              disabled={testing}
              className="flex-1 py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all disabled:opacity-50"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              onClick={save}
              className="flex-1 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-all"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              {saved ? '✓ Saved' : 'Save Settings'}
            </button>
          </div>

          {testResult && (
            <div
              className={`mt-4 p-3 rounded-lg text-sm font-medium ${
                testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {testResult.ok ? '✓ ' : '✗ '}{testResult.msg}
            </div>
          )}

          {/* Info */}
          <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">About AI keys</h3>
            <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
              <li>Keys are stored locally on your machine, encrypted at rest</li>
              <li>Never shared with any server or third party</li>
              <li>Claude claude-opus-4-8 recommended for best TSCV results</li>
              <li>GPT-4o is a good alternative with comparable quality</li>
              <li>Azure OpenAI uses your enterprise Azure resource (endpoint + deployment)</li>
            </ul>
          </div>

          {/* License Info */}
          {licenseInfo && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">License</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">License Type</span>
                  <span
                    className={`px-2 py-0.5 rounded-full font-semibold capitalize ${
                      licenseInfo.licenseType === 'trial'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    {licenseInfo.licenseType}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Licensed to</span>
                  <span className="font-medium text-gray-700">{licenseInfo.recipientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Organization</span>
                  <span className="font-medium text-gray-700">{licenseInfo.organizationName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Expires</span>
                  <span className="font-medium text-gray-700">
                    {new Date(licenseInfo.expiresAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Projects</span>
                  <span className="font-medium text-gray-700">
                    {licenseStatus?.maxDashboards ?? 'Unlimited'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Features</span>
                  <span className="font-medium text-gray-700">{licenseInfo.features.join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Machine Bound</span>
                  <span className={`font-medium ${licenseInfo.machineBound ? 'text-green-600' : 'text-amber-600'}`}>
                    {licenseInfo.machineBound ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <button
                onClick={async () => {
                  if (!confirm('This will remove the current license. You will need a new license file to continue using the app.')) return
                  await api.resetLicense()
                  await refreshLicense()
                  navigate('/activate', { replace: true })
                }}
                className="mt-3 w-full py-2 px-4 rounded-lg border-2 border-red-200 text-xs font-semibold text-red-500 hover:bg-red-50 transition-all"
              >
                Replace License
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
